import { useState } from 'react';
import { usePlaytesterStore } from '../../stores/playtester-store.js';

export default function CounterModal() {
  const contextMenu = usePlaytesterStore((s) => s.contextMenu);
  const battlefield = usePlaytesterStore((s) => s.battlefield);
  const command = usePlaytesterStore((s) => s.command);
  const addCounter = usePlaytesterStore((s) => s.addCounter);
  const setCounter = usePlaytesterStore((s) => s.setCounter);
  const removeCounter = usePlaytesterStore((s) => s.removeCounter);
  const closeCounterModal = usePlaytesterStore((s) => s.closeCounterModal);

  const [newCounterName, setNewCounterName] = useState('');
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  if (!contextMenu) return null;

  const { instanceId } = contextMenu;

  // Find the card instance in battlefield or command zone
  const instance =
    battlefield.find((i) => i.instanceId === instanceId) ??
    command.find((i) => i.instanceId === instanceId);

  if (!instance) return null;

  const counters = instance.counters;
  const counterEntries = Object.entries(counters);

  function handleClose() {
    closeCounterModal();
  }

  function handleSetValue(name: string, rawValue: string) {
    setEditValues((prev) => ({ ...prev, [name]: rawValue }));
    const v = parseInt(rawValue, 10);
    if (!isNaN(v)) {
      setCounter(instanceId, name, v);
    }
  }

  function handleAddNew() {
    const name = newCounterName.trim();
    if (!name) return;
    addCounter(instanceId, name, 1);
    setNewCounterName('');
  }

  const PRESET_COUNTERS = ['+1/+1', '-1/-1', 'loyalty', 'poison', 'charge', 'fade', 'lore'];

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[360px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <h2 className="text-sm font-semibold text-white">
            Counters — <span className="text-gray-400 font-normal">{instance.card.name}</span>
          </h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-white transition text-lg leading-none">
            ✕
          </button>
        </div>

        <div className="p-4 flex flex-col gap-4">
          {/* Existing counters */}
          {counterEntries.length > 0 ? (
            <div className="flex flex-col gap-2">
              {counterEntries.map(([name, value]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-gray-200 font-medium">{name}</span>
                  <button
                    onClick={() => addCounter(instanceId, name, -1)}
                    className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-bold transition"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={editValues[name] ?? value}
                    onChange={(e) => handleSetValue(name, e.target.value)}
                    onFocus={() => setEditValues((prev) => ({ ...prev, [name]: String(value) }))}
                    className="w-12 text-center text-sm bg-gray-800 border border-gray-700 rounded text-white outline-none focus:border-orange-500 transition"
                  />
                  <button
                    onClick={() => addCounter(instanceId, name, 1)}
                    className="w-6 h-6 flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-bold transition"
                  >
                    +
                  </button>
                  <button
                    onClick={() => removeCounter(instanceId, name)}
                    className="text-gray-600 hover:text-red-400 transition text-xs px-1"
                    title="Remove counter type"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-600 text-center py-1">No counters yet</div>
          )}

          {/* Add new counter type */}
          <div className="border-t border-gray-800 pt-3 flex flex-col gap-2">
            <span className="text-xs text-gray-500 font-medium">Add counter type</span>
            {/* Presets */}
            <div className="flex flex-wrap gap-1">
              {PRESET_COUNTERS.filter((p) => !counters[p]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => addCounter(instanceId, preset, 1)}
                  className="px-2 py-0.5 text-[10px] rounded bg-gray-800 border border-gray-700 hover:border-orange-500 text-gray-400 hover:text-white transition"
                >
                  {preset}
                </button>
              ))}
            </div>
            {/* Custom */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Custom counter name"
                value={newCounterName}
                onChange={(e) => setNewCounterName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-orange-500 transition"
              />
              <button
                onClick={handleAddNew}
                disabled={!newCounterName.trim()}
                className="px-3 py-1 text-sm font-medium bg-orange-600 hover:bg-orange-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded transition"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
